import React, { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import FilterBar from '../../components/shared/FilterBar';
import MarketingListingCard from '../../components/home/MarketingListingCard';
import MarketingBrowseEmpty from '../../components/home/MarketingBrowseEmpty';
import MarketingBrowseShell, {
  MarketingBrowseGrid,
  MarketingBrowseSkeleton,
} from '../../components/home/MarketingBrowseShell';
import { useCategoryServices } from '../../hooks/useCategoryServices';
import { useServiceCategory } from '../../services/serviceCategoryService';
import useUserLocation from '../../hooks/useUserLocation';

const CategoryServices = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const { userLocation } = useUserLocation();

  const { data: categoryResponse, isLoading: categoryLoading } =
    useServiceCategory(category);
  const categoryData = categoryResponse?.data || categoryResponse;
  const categoryTitle =
    searchParams.get('title') ||
    categoryData?.category_name ||
    category?.replace(/-/g, ' ');

  const {
    services: allServices,
    isLoading: servicesLoading,
    error,
  } = useCategoryServices(category, {
    per_page: 100,
    sort_by:
      userLocation?.latitude && userLocation?.longitude
        ? 'distance'
        : 'created_at',
    sort_order:
      userLocation?.latitude && userLocation?.longitude ? 'asc' : 'desc',
    location: userLocation,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const filteredServices = useMemo(() => {
    let filtered = [...allServices].filter(
      (service) => service?.id && (service.title || service.service_name)
    );

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (service) =>
          service.title?.toLowerCase().includes(q) ||
          service.vendor?.toLowerCase().includes(q) ||
          service.location?.toLowerCase().includes(q)
      );
    }

    if (selectedPriceRange !== 'all') {
      const [min, max] = selectedPriceRange.split('-').map(Number);
      filtered = filtered.filter((service) => {
        const price = service.price || 0;
        if (max) return price >= min && price <= max;
        return price >= min;
      });
    }

    if (selectedRating !== 'all') {
      const minRating = parseFloat(selectedRating.replace('+', ''));
      filtered = filtered.filter((service) => (service.rating || 0) >= minRating);
    }

    return filtered;
  }, [allServices, searchTerm, selectedPriceRange, selectedRating]);

  const isLoading = categoryLoading || servicesLoading;
  const countLabel = isLoading
    ? 'Loading…'
    : `${filteredServices.length} service${filteredServices.length === 1 ? '' : 's'}`;

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedPriceRange('all');
    setSelectedRating('all');
  };

  const filterSlot = (
    <FilterBar
      selectedPriceRange={selectedPriceRange}
      setSelectedPriceRange={setSelectedPriceRange}
      selectedRating={selectedRating}
      setSelectedRating={setSelectedRating}
      showMoreFilters={showMoreFilters}
      setShowMoreFilters={setShowMoreFilters}
      showCategoryFilter={false}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      clearAllFilters={clearFilters}
    />
  );

  return (
    <MarketingBrowseShell
      mobileTitle={categoryTitle || 'Category'}
      title={categoryTitle || 'Services'}
      countLabel={countLabel}
      filterSlot={filterSlot}
    >
      {isLoading ? (
        <MarketingBrowseSkeleton />
      ) : error ? (
        <MarketingBrowseEmpty
          title="Failed to load services"
          description="Please try again later."
          onClear={null}
          secondaryHref="/browse-vendors"
          secondaryLabel="Browse vendors"
        />
      ) : filteredServices.length === 0 ? (
        <MarketingBrowseEmpty
          title="No services found"
          description="Try adjusting your filters, or browse all vendors."
          onClear={clearFilters}
        />
      ) : (
        <MarketingBrowseGrid>
          {filteredServices.map((service) => (
            <MarketingListingCard
              key={service.id}
              href={`/services/${service.id}`}
              image={service.image}
              title={service.title}
              subtitle={service.vendor}
              location={service.location}
              rating={service.rating}
              price={service.price}
              priceSuffix="starting from"
              reviewCount={service.reviewCount}
              featured={service.isFeatured}
            />
          ))}
        </MarketingBrowseGrid>
      )}
    </MarketingBrowseShell>
  );
};

export default CategoryServices;
